import React, { useState, useReducer, useContext, useMemo, useCallback, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'

import Grid from '@material-ui/core/Grid'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import Divider from '@material-ui/core/Divider'
import Select from '@material-ui/core/Select'
import InputLabel from '@material-ui/core/InputLabel'

import Button from '@material-ui/core/Button'
import IconButton from '@material-ui/core/IconButton'


import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import FormControl from '@material-ui/core/FormControl'

import FormLabel from '@material-ui/core/FormLabel'
import FormGroup from '@material-ui/core/FormGroup'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Checkbox from '@material-ui/core/Checkbox'
import TextField from '@material-ui/core/TextField'

import SaveIcon from '@material-ui/icons/Save'
import DeleteIcon from '@material-ui/icons/Delete'

import godJson from '../game-data/gods.json'

import { AppContext } from './context'
import BoonCard from './boon-card'


const useStyles = makeStyles((theme) => ({
  container: {
    width: '100%',
    height: '100vh',
  },
  paddedPaper: { padding: theme.padding },
  armsContainer: {
    width: '100%',
    padding: theme.padding,
  },
  formControl: { width: '100%' },
  boonContainer: {
    width: '100%',
    display: 'flex',
  },
  slotContainer: {
    padding: theme.padding,
    flexGrow: 1,
  },
  otherBoonContainer: {
    flexGrow: 5,
    marginLeft: theme.margin,
    display: 'flex',
    flexDirection: 'column'
  },
  otherBoons: {
    padding: theme.padding,
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-evenly',
    '& > div': {
      marginBottom: theme.margin,
      width: '45%',
    }
  },
  otherBoonOptionContainer:{
    padding: theme.padding,
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-evenly',
    '& > div': {
      marginBottom: theme.margin,
      width: '30%',
    },
  },
  placeholderImg: { maxHeight: '50px' },
  godImg: { maxHeight: '50px' },
  affinityBar: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.padding,
    margin: theme.margin,
    border: '2px groove black',
    borderRadius: '5px',
    color: 'black'
  },
  affinityName: {
    display: 'flex',
    alignItems: 'center',
  },
  effectName: {
    display: 'flex',
    alignItems: 'center',
  },
  godCheckedImg: { maxHeight: '30px' },
  godUncheckedImg: {
    maxHeight: '30px',
    opacity: 0.5
  },
  flexDiv: { display: 'flex' },
  disabledOption: { color: 'grey' },
  slotMenuItem: {
    display: 'flex',
    '& > button': {
      marginLeft: theme.margin,
      alignSelf: 'flex-end'
    }
  }
}))

const getCurrentBoons = (boons, build) => Object.values(boons)
  .reduce((agg, o) => {
    const currentRestrictions = getRestrictionsMap(o.restrictions, build)
    const currentPrerequisites = getPrerequisitesMap(o.prerequisites, build)
    agg[o.name] = {
      ...o,
      currentRestrictions,
      currentPrerequisites,
      valid: currentRestrictions.valid && currentPrerequisites.valid,
    }
    return agg
  }, {})

const getRestrictionsMap = (restrictions, fullBuild) => {
  if (!restrictions || !restrictions.length) { return { valid: true } }
  const values = restrictions.map(name => ({ name, valid: !fullBuild[name]?.have }))
  return {
    valid: values.every(o => o.valid),
    values,
  }
}

const getPrerequisitesMap = (prerequisites, fullBuild) => {
  if (!prerequisites) { return { valid: true } }
  const { type, values } = prerequisites

  let current_prereqs = []
  /* ====[PREREQ]
   - true is valid

   one of
   one of each
   two of three
   unique to

  */
  if (type === 'one of') {
    // [a, b, c, d] a or b or c or d
    current_prereqs = {
      type,
      valid: values.some(target => {
        if (typeof target === 'object') {
          // ====[NOTE] the one { type: "all" ...} case is Poseidon
          // ====[TODO] better support for recursive
          return getPrerequisitesMap(target, fullBuild)
        }
        return fullBuild[target]?.have
      }),
      values: values.map(target => {
        if (typeof target === 'object') {
          // ====[NOTE] the one { type: "all" ...} case is Poseidon
          // ====[TODO] better support for recursive
          return getPrerequisitesMap(target, fullBuild)
        }
        return { name: target, valid: fullBuild[target]?.have }
      })
    }
  } else if (type === 'all') {
    current_prereqs = {
      type,
      valid: values.every(target => fullBuild[target]?.have),
      values: values.map(target => ({ name: target, valid: fullBuild[target]?.have })),
    }
  } else if (type === 'one of each') {
    // [[a, b], [c, d]] a or b AND c or d
    current_prereqs = {
      type,
      valid: values.every(boons => boons.some(target => fullBuild[target]?.have)),
      values: values.map(boons => ({
        type: 'one of',
        valid: boons.some(target => fullBuild[target]?.have), values: boons.map(target => ({ name: target, valid: fullBuild[target]?.have }))
      }))
    }
  } else if (type === 'two of three') {
    // [a,b,c] two matches
    current_prereqs = {
      type,
      valid: values.map(target => fullBuild[target]?.have).filter(o => o).length >= 2,
      values: values.map(target => ({ name: target, valid: fullBuild[target]?.have })),
    }
  } else if (type === 'unique to') {
    // ====[NOTE] this is really just 'one of' with a single option
    current_prereqs = {
      type,
      valid: values.some(target => fullBuild[target]?.have),
      values: values.map(target => ({ name: target, valid: fullBuild[target]?.have }))
    }
  }
  return current_prereqs
}

/*
  next steps
  - recalc targetAffinity with prereqs
  - calculate god "priority" with prereqs
  - chaos curses: https://hades.gamepedia.com/File:Chaos_Curse_Pauper.png
*/
const Builder = () => {
  const classes = useStyles()
  const { arms, slots, boons: boonJSON } = useContext(AppContext)

  const [thisBuild, dispatch] = useReducer((state, { type, action, payload }) => {
    if (type === 'load') {
      const build = Object.entries(boonJSON).reduce((agg, [name, details]) => ({
        ...agg,
        [name]: {
          ...details,
          have: action === 'want' ? false : payload.build.boons[name]?.have,
          want: ((payload.build.boons[name]?.want || payload.build.boons[name]?.have) && action === 'want') ? true : payload.build.boons[name]?.want,
        }
      }), {})
      return {
        arm: payload.build.arm || '',
        aspect: payload.build.aspect || '',
        boons: getCurrentBoons(build, { ...build, [payload.build.arm || '']: { have: true }, [payload.build.aspect || '']: { have: true } })
      }
    }
    let update
    if (type === 'arm') {
      update = {
        ...state,
        arm: payload,
        aspect: '',
      }
    } else if (type === 'aspect') {
      update = {
        ...state,
        aspect: payload,
      }
    } else if (type === 'boon') {
      const { boon, value } = payload
      // { 'boon', 'have', true }
      let slotBoons = {}
      const slot = state.boons[boon].slot
      if (slot && value) {
        // ====[NOTE] reset all values for the slot
        slotBoons = Object.values(state.boons).filter(o => o.slot === slot).reduce((agg, o) => ({
          ...agg,
          [o.name]: {
            ...o,
            [action]: false,
          }
        }), {})
      }

      let boonUpdate = {}
      if ((action === 'have' && value) || (action === 'want' && value && state.boons[boon].have)) {
        boonUpdate.want = false
      }
      update = {
        ...state,
        boons: {
          ...state.boons,
          ...slotBoons,
          [boon]: {
            ...state.boons[boon],
            [action]: value,
            ...boonUpdate,
          }
        }
      }
    }

    return {
      ...update,
      boons: getCurrentBoons(update.boons, { ...update.boons, [update.arm]: { have: true }, [update.aspect]: { have: true } })
    }
  }, {
    arm: '',
    aspect: '',
    boons: getCurrentBoons(boonJSON, boonJSON)
  })

  const handleArmChange = (e) => {
    dispatch({ type: 'arm', payload: e.target.value })
  }
  const handleAspectChange = (e) => {
    dispatch({ type: 'aspect', payload: e.target.value })
  }
  const handleBoonAdd = (boon) => () => {
    dispatch({ type: 'boon', action: 'have', payload: { boon, value: true } })
  }
  const handleBoonRemove = (boon) => () => {
    dispatch({ type: 'boon', action: 'have', payload: { boon, value: false } })
  }
  const handleWantBoonAdd = (boon) => () => {
    dispatch({ type: 'boon', action: 'want', payload: { boon, value: true } })
  }
  const handleWantBoonRemove = (boon) => () => {
    dispatch({ type: 'boon', action: 'want', payload: { boon, value: false } })
  }

  const {
    currentSlots,
    currentOther,
    currentOtherWant,
    currentOtherOptions,
  } = useMemo(() => Object.values(thisBuild.boons).reduce((agg, o) => {
    if (o.slot) {
      // { [slot]: { have, want, options }}
      if (!agg.currentSlots[o.slot]) {
        agg.currentSlots[o.slot] = { have: '', want: '', options: [] }
      }
      if (o.have) {
        agg.currentSlots[o.slot].have = o.name
      }
      if (o.want) {
        agg.currentSlots[o.slot].want = o.name
      }
      agg.currentSlots[o.slot].options.push(o)
    } else if (o.have) {
      agg.currentOther.push(o)
    } else if (o.want) {
      agg.currentOtherWant.push(o)
    } else {
      agg.currentOtherOptions.push(o)
    }

    return agg
  }, { currentSlots: {}, currentOther: [], currentOtherWant: [], currentOtherOptions: [] }), [thisBuild.boons])

  const [slotMenuAnchors, setSlotMenuAnchors] = useState({})
  const handleSlotBoonOptionsOpen = slot => e => {
    setSlotMenuAnchors(o => ({ ...o, [slot]: e.currentTarget }))
  }
  const handleSlotBoonOptionsClose = slot => () => {
    setSlotMenuAnchors(o => ({ ...o, [slot]: false }))
  }
  const handleSlotBoonAdd = (name, slot) => () => {
    handleBoonAdd(name)()
    handleSlotBoonOptionsClose(slot)()
  }
  const handleSlotWantBoonAdd = (name, slot) => () => {
    handleWantBoonAdd(name)()
    handleSlotBoonOptionsClose(slot)()
  }

  // [{ name: , count: }]
  const { affinities: currentAffinities, totalAffinity: currentTotalAffinity, effects: currentEffects ,totalEffects: currentTotalEffects, } = useMemo(() => {
    const { gods, totalGods, effects: e, totalEffects } = Object.values(thisBuild.boons).filter(o => o.have).reduce((agg, boon) => {
      if (boon.gods) {
        boon.gods.forEach(god => {
          agg.totalGods += 1
          agg.gods[god] = (agg.gods[god] || 0) + 1
        })
      }
      if (boon.effects) {
        // ====[TODO] how does 'type' affect value calc?
        boon.effects.forEach(({ name, type, value }) => {
          agg.totalEffects += 1
          agg.effects[name] = (agg.effects[name] || 0) + value
        })
      }
      return agg
    }, { gods: {}, totalGods: 0, effects: {}, totalEffects: 0, })

    const affinities = Object.entries(gods).map(([name, count]) => ({ name, count }))
    affinities.sort((a,b) => b.count - a.count)
    const effects = Object.entries(e).map(([name, count]) => ({ name, count }))
    effects.sort((a,b) => b.count - a.count)
    return { affinities, totalAffinity: totalGods, effects, totalEffects }
  }, [thisBuild])

  const { affinities: targetAffinities, totalAffinity: targetTotalAffinity, effects: targetEffects, totalEffects: targetTotalEffects, } = useMemo(() => {
    const { gods, totalGods, effects: e, totalEffects } = Object.values(thisBuild.boons).filter(o => o.want).reduce((agg, boon) => {
      if (boon.gods) {
        boon.gods.forEach(god => {
          agg.totalGods += 1
          agg.gods[god] = (agg.gods[god] || 0) + 1
        })
      }
      if (boon.effects) {
        // ====[TODO] how does 'type' affect value calc?
        boon.effects.forEach(({ name, type, value }) => {
          agg.totalEffects += 1
          agg.effects[name] = (agg.effects[name] || 0) + value
        })
      }
      return agg
    }, { gods: {}, totalGods: 0, effects: {}, totalEffects: 0 })

    const affinities = Object.entries(gods).map(([name, count]) => ({ name, count }))
    affinities.sort((a,b) => b.count - a.count)
    const effects = Object.entries(e).map(([name, count]) => ({ name, count }))
    effects.sort((a,b) => b.count - a.count)
    return { affinities, totalAffinity: totalGods, effects, totalEffects }
  }, [thisBuild])

  // { gods: [{ name: , count: }], boons: [{ name:, count: }]}
  // prereqs for current INVALID WANT
  // plus current valid
  const { godPrereqs, boonPrereqs } = useMemo(() => {
    const { gods, boons } = Object.values(thisBuild.boons)
      .filter(o => o.want && o.currentPrerequisites)
      .reduce((agg, boon) => {
        if (boon.valid) {
          agg.boons[boon.name] = (agg.boons[boon.name] || 0) + 1
          boonJSON[boon.name].gods.forEach(godName => {
            agg.gods[godName] = (agg.gods[godName] || 0) + 1
          })
        } else {
          const { type, values } = boon.currentPrerequisites
          if (type === 'one of') {
            // god should be max + 1
            const foundGods = {}
            values.filter(o => !o.valid && boonJSON[o.name]).map(o => o.name).forEach(boonName => {
              if (typeof boonName === 'object') {
                // ====[NOTE] it's just HUGE CATCH
                // =========] this doesn't account for having conch shell
                // ====[TODO] recursion here or restructure
                const { values } = boonName
                values.filter(o => !o.valid && boonJSON[o.name]).map(o => o.name).forEach(boonName => {
                  agg.boons[boonName] = (agg.boons[boonName] || 0) + 1
                  boonJSON[boonName].gods.forEach(godName => {
                    agg.gods[godName] = (agg.gods[godName] || 0) + 1
                  })
                })
              } else {
                agg.boons[boonName] = (agg.boons[boonName] || 0) + 1
                boonJSON[boonName].gods.forEach(godName => {
                  if (!foundGods[godName]) {
                    foundGods[godName] = true
                    agg.gods[godName] = (agg.gods[godName] || 0) + 1
                  }
                })
              }
            })
          } else if (type === 'two of three') {
            // god should be + 2
            const foundGods = {}
            values.filter(o => !o.valid && boonJSON[o.name]).map(o => o.name).forEach(boonName => {
              agg.boons[boonName] = (agg.boons[boonName] || 0) + 1
              boonJSON[boonName].gods.forEach(godName => {
                if (foundGods[godName] < 2) {
                  foundGods[godName] = (foundGods[godName] || 0) + 1
                  agg.gods[godName] = (agg.gods[godName] || 0) + 1
                }
              })
            })
          } else if (type === 'one of each') {
            values.filter(o => !o.valid).forEach(({ valid, values }) => {
              const foundGods = {}
              values.filter(o => !o.valid && boonJSON[o.name]).map(o => o.name).forEach(boonName => {
                agg.boons[boonName] = (agg.boons[boonName] || 0) + 1
                boonJSON[boonName].gods.forEach(godName => {
                  if (!foundGods[godName]) {
                    foundGods[godName] = true
                    agg.gods[godName] = (agg.gods[godName] || 0) + 1
                  }
                })
              })
            })
          }
        }
        
        return agg
      }, { gods: {}, boons: {} })

    const godPrereqs = Object.entries(gods).map(([name, count]) => ({ name, count }))
    godPrereqs.sort((a,b) => b.count - a.count)
    const boonPrereqs = Object.entries(boons).map(([name, count]) => ({ name, count }))
    boonPrereqs.sort((a,b) => b.count - a.count)
    return { godPrereqs, boonPrereqs }
  }, [thisBuild, boonJSON])

  const [godFilter, setGodFilter] = useState(Object.keys(godJson).reduce((agg, name) => ({ ...agg, [name]: true }), {}))
  const handleGodFilterChange = (event) => {
    setGodFilter(o => ({
      ...o,
      [event.target.name]: event.target.checked,
    }))
  }
  const allGods = useMemo(() => Object.values(godFilter).every(o => o), [godFilter])
  const handleGodFilterToggle = useCallback(() => {
    if (allGods) {
      setGodFilter(Object.keys(godJson).reduce((agg, name) => ({ ...agg, [name]: false }), {}))
    } else {
      setGodFilter(Object.keys(godJson).reduce((agg, name) => ({ ...agg, [name]: true }), {}))
    }
  }, [allGods])

  const [boonFilter, setBoonFilter] = useState('')
  const handleOnBoonFilterChange = e => {
    setBoonFilter(e.target.value)
  }

  const filteredOtherOptions = useMemo(() => currentOtherOptions
    .filter(({ gods }) => gods.some(god => Object.entries(godFilter).filter(o => o[1]).map(o => o[0]).includes(god)))
    .filter(({ name }) => name.toLowerCase().indexOf(boonFilter.toLowerCase()) >= 0), [currentOtherOptions, godFilter, boonFilter])

  const [saveMenuAnchor, setSaveMenuAnchor] = useState()
  const handleSaveMenuOpen = e => setSaveMenuAnchor(e.target)
  const handleSaveMenuClose = e => setSaveMenuAnchor(false)
  const [savedBuilds, setSavedBuilds] = useState([])
  useEffect(() => {
    const local = localStorage.getItem('builds')
    if (local) {
      setSavedBuilds(JSON.parse(local))
    }
  }, [])
  const [buildName, setBuildName] = useState('')
  const handleOnBuildNameChange = e => setBuildName(e.target.value)
  const handleSaveBuild = useCallback(() => {
    const saveBuild = {
      arm: thisBuild.arm,
      aspect: thisBuild.aspect,
      boons: Object.entries(thisBuild.boons).filter(o => o[1].have || o[1].want).reduce((agg, o) => ({
        ...agg,
        [o[0]]: { have: o[1].have, want: o[1].want }
      }), {})
    }
    const save = { name: buildName, build: saveBuild }
    localStorage.setItem('builds', JSON.stringify([...savedBuilds, save]))
    setSavedBuilds(o => ([...o, save]))
  }, [thisBuild, buildName, savedBuilds])

  const handleLoadBuild = build => () => {
    dispatch({ type: 'load', payload: { build } })
  }
  const handleLoadBuildWant = build => () => {
    dispatch({ type: 'load', action: 'want', payload: { build } })
  }
  const handleDeleteBuild = i => () => {
    setSavedBuilds(o => {
      // ====[TODO] is splice here "alright"?
      const update = o.splice(i, 0)
      localStorage.setItem('builds', JSON.stringify(update))
      return update
    })
  }

  return (
    <div className={classes.container} >
      <IconButton onClick={handleSaveMenuOpen}>
        <SaveIcon />
      </IconButton>
      <Menu
        id='save-menu'
        anchorEl={saveMenuAnchor}
        keepMounted
        getContentAnchorEl={null}
        open={Boolean(saveMenuAnchor)}
        onClose={handleSaveMenuClose}
      >
        <MenuItem disableRipple>
          <TextField label='Build Name' variant='outlined' value={buildName} onChange={handleOnBuildNameChange} />
          <Button onClick={handleSaveBuild}>Save</Button>
        </MenuItem>
        {savedBuilds.map((o, i) => (
          <MenuItem disableRipple key={o.name}>
            {o.name}
            <Button onClick={handleLoadBuild(o.build)}>Load (exact)</Button>
            <Button onClick={handleLoadBuildWant(o.build)}>Load (all want)</Button>
            <IconButton onClick={handleDeleteBuild(i)}><DeleteIcon /></IconButton>
          </MenuItem>
        ))}
      </Menu>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper className={classes.paddedPaper}>
            <Grid container spacing={2}>
              <Grid container item xs={3}>
                <Grid item xs={12}>
                  <Typography variant='h5'>Boon Priority</Typography>
                  <Divider />
                </Grid>
                <Grid item xs={4}>
                  {godPrereqs.map(o => (
                    <div key={o.name} className={classes.flexDiv}>
                      <div className={classes.affinityName}>
                        <img alt={o.name} src={godJson[o.name].symbol_url} className={classes.godImg} />
                        {o.count}
                      </div>
                    </div>
                  ))}    
                </Grid>
                <Grid item xs={8}>
                  {boonPrereqs.map(o => (
                    <div key={o.name}>
                      {o.name}: {o.count}
                    </div>
                  ))}
                </Grid>
              </Grid>
              <Grid item xs={5} container spacing={1}>
                <Grid item xs={12}><Typography variant='h5'>Current</Typography><Divider /></Grid>
                <Grid item xs={8}>
                  <Typography variant='h6'>Affinities</Typography>
                  <Divider light />
                  {!currentAffinities.length && (<>
                    <img alt='no boon'
                      className={classes.placeholderImg}
                      src='https://static.wikia.nocookie.net/hades_gamepedia_en/images/e/e3/Cerberus-bond-forged.png'
                    />
                    <Typography variant='body1'>No Boons</Typography>
                  </>)}
                  {currentAffinities.map(o => (
                    <div key={o.name} className={classes.flexDiv}>
                      <div className={classes.affinityName}>
                        <img alt={`${o.name}`} src={godJson[o.name].symbol_url} className={classes.godImg} />
                      </div>
                      <div
                        className={classes.affinityBar}
                        style={{
                          width: `${Math.round(100 * o.count / currentTotalAffinity)}%`,
                          backgroundColor: godJson[o.name].color,
                        }}
                      >
                        {o.count}
                      </div>
                    </div>
                  ))}
                </Grid>
                <Grid item xs={4}>
                  <Typography variant='h6'>Effects</Typography>
                  <Divider light />
                  {currentEffects.map(o => (
                    <div key={o.name} className={classes.flexDiv}>
                      <div className={classes.effectName}>
                        {o.name}
                      </div>
                      <div
                        className={classes.affinityBar}
                        style={{
                          width: `${Math.round(100 * o.count / currentTotalEffects)}%`,
                          backgroundColor: godJson['Chaos'].color,
                        }}
                      >
                        {o.count}
                      </div>
                    </div>
                  ))}
                </Grid>
              </Grid>
              <Grid item xs={4} container spacing={1}>
                <Grid item xs={12}><Typography variant='h5'>Target</Typography><Divider light /></Grid>
                <Grid item xs={8}>
                  <Typography variant='h6'>Affinities</Typography>
                  <Divider light />
                  {!targetAffinities.length && (<>
                    <img
                      alt='no boon'
                      className={classes.placeholderImg}
                      src='https://static.wikia.nocookie.net/hades_gamepedia_en/images/e/e3/Cerberus-bond-forged.png'
                    />
                    <Typography variant='body1'>No Target Boons</Typography>
                  </>)}
                  {targetAffinities.map(o => (
                    <div key={o.name} className={classes.flexDiv}>
                      <div className={classes.affinityName}>
                        <img alt={o.name} src={godJson[o.name].symbol_url} className={classes.godImg} />
                        {o.name}
                      </div>
                      <div
                        className={classes.affinityBar}
                        style={{
                          width: `${Math.round(100 * o.count / targetTotalAffinity)}%`,
                          backgroundColor: godJson[o.name].color,
                        }}
                      >
                        {o.count}
                      </div>
                    </div>
                  ))}
                </Grid>
                <Grid item xs={4}>
                  <Typography variant='h6'>Effects</Typography>
                  <Divider light />
                  {targetEffects.map(o => (
                    <div key={o.name} className={classes.flexDiv}>
                      <div className={classes.effectName}>
                        {o.name}
                      </div>
                      <div
                        className={classes.affinityBar}
                        style={{
                          width: `${Math.round(100 * o.count / targetTotalEffects)}%`,
                          backgroundColor: godJson['Chaos'].color,
                        }}
                      >
                        {o.count}
                      </div>
                    </div>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper className={classes.paddedPaper}>
            <div className={classes.armsContainer}>
              <Typography variant='h4'>
                Weapon
              </Typography>
              <Divider light />
              <FormControl className={classes.formControl}>
                <InputLabel id='infernal-arm-label'>Choose an Infernal Arm</InputLabel>
                <Select
                  labelId='infernal-arm-label'
                  id='infernal-arm'
                  value={thisBuild.arm}
                  onChange={handleArmChange}
                  renderValue={(selected) => (
                    <div>
                      {/* TODO display the image as well */}
                      {selected}
                    </div>
                  )}
                >
                  {Object.values(arms).map((arm) => (
                    <MenuItem key={arm.name} value={arm.name}>
                      {arm.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl className={classes.formControl}>
                <InputLabel id='aspect-label'>Choose an Aspect</InputLabel>
                <Select
                  labelId='aspect-label'
                  id='aspect-arm'
                  disabled={thisBuild.arm === ''}
                  value={thisBuild.aspect}
                  onChange={handleAspectChange}
                  renderValue={(selected) => (
                    <div>
                      {/* TODO display the image as well */}
                      {selected}
                    </div>
                  )}
                >
                  {thisBuild.arm !== '' && arms[thisBuild.arm].aspects.map((aspect) => (
                    <MenuItem key={aspect.name} value={aspect.name}>
                      {aspect.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className={classes.slotContainer}>
              <Typography variant='h4'>
                Abilities
              </Typography>
              {slots.map(slot => (
                <BoonCard
                  key={slot}
                  slot={slot}
                  haveClick={handleBoonAdd}
                  removeClick={thisBuild.boons[currentSlots[slot].have] ? handleBoonRemove : false}
                  {...thisBuild.boons[currentSlots[slot].have] || {}}
                  wantBoon={thisBuild.boons[currentSlots[slot].want]}
                >
                  <Button aria-controls={`${slot}-menu`} onClick={handleSlotBoonOptionsOpen(slot)}>
                    Choose {slot} Boon
                  </Button>
                  <Menu
                    id={`${slot}-menu`}
                    anchorEl={slotMenuAnchors[slot]}
                    keepMounted
                    getContentAnchorEl={null}
                    open={Boolean(slotMenuAnchors[slot])}
                    onClose={handleSlotBoonOptionsClose(slot)}
                  >
                    {currentSlots[slot].options.filter(o => o.valid).map(({ name }) => (
                      <MenuItem dense disabled={thisBuild.boons[name].have} disableRipple key={name} value={name}>
                        <div className={classes.slotMenuItem}>
                          {name}
                          <Button variant='contained' size='small' color='primary' onClick={handleSlotBoonAdd(name, slot)}>HAVE</Button>
                          <Button variant='contained' size='small' color='primary' onClick={handleSlotWantBoonAdd(name, slot)}>WANT</Button>
                        </div>
                      </MenuItem>
                    ))}
                  </Menu>
                </BoonCard>
              ))}
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} md={9}>
          <Paper className={classes.paddedPaper}>
            <Typography variant='h4'>
              Augments
            </Typography>
            <Divider light />
            <div className={classes.boonContainer}>
              <div className={classes.otherBoonContainer}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant='h5'>Current</Typography>
                    <div className={classes.otherBoons}>
                      {currentOther.map(({ name }) => (<BoonCard
                        key={name}
                        removeClick={handleBoonRemove}
                        {...thisBuild.boons[name]}
                      />))}
                    </div>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant='h5'>Target</Typography>
                    <div className={classes.otherBoons}>
                      {currentOtherWant.map(({ name, valid }) => (<BoonCard
                        key={name}
                        removeClick={handleWantBoonRemove}
                        haveClick={valid ? handleBoonAdd : false}
                        placeholder
                        {...thisBuild.boons[name]}
                      />))}
                    </div>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <div className={classes.optionFilters}>
                      {/* god checkbox filters, effect checkbox filters, string name search */}
                      <FormControl component='fieldset' className={classes.godFormControl}>
                        <FormLabel component='legend'>Filter Gods</FormLabel>
                        <FormGroup row>
                          {Object.entries(godJson).map(([name, { symbol_url }]) => (
                            <FormControlLabel
                              control={<Checkbox
                                checked={godFilter[name] || false}
                                onChange={handleGodFilterChange}
                                name={name}
                                icon={<img alt={name} src={symbol_url} className={classes.godUncheckedImg} />}
                                checkedIcon={<img alt={name} src={symbol_url} className={classes.godCheckedImg} />}
                              />}
                              label={name}
                              key={name}
                              classes={{ label: clsx({ [classes.disabledOption]: !godFilter[name] }) }}
                            />
                          ))}
                          <Button onClick={handleGodFilterToggle}>{allGods ? 'None' : 'All'}</Button>
                        </FormGroup>
                      </FormControl>
                      <TextField id='boon-name-filter' label='Filter by Boon name' variant='outlined' value={boonFilter} onChange={handleOnBoonFilterChange} />
                  </div>
                  <Typography variant='h5'>Options</Typography>
                  <div className={classes.otherBoonOptionContainer}>
                    {filteredOtherOptions.map(({ name, valid }) => (<BoonCard
                      key={name}
                      wantClick={handleWantBoonAdd}
                      haveClick={valid ? handleBoonAdd : false}
                      {...thisBuild.boons[name]}
                    />))}
                  </div>
                </Grid>
              </div>
            </div>                      
          </Paper>
        </Grid>
      </Grid>
    </div>
  )
}

export default Builder
